import {
  Card,
  FormLayout,
  TextField,
  Select,
  Button,
  Stack,
  ButtonGroup,
  Text,
  Tooltip,
  Icon,
  Banner,
  Toast
} from '@shopify/polaris';
import {
  QuestionMarkMinor,
  SaveMinor,
  RefreshMinor
} from '@shopify/polaris-icons';
import React, { useState, useEffect } from 'react';
import { UserSettingsService } from '../../services/userSettingsService';

export default function PricingTools({
  priceTools,
  setPriceTools,
  selectedProductIds,
  products,
  onPriceUpdate,
  isPriceUpdating,
  priceChange,
  setPriceChange,
  priceChangeType,
  setPriceChangeType,
  editHistory,
  setEditHistory,
  user
}) {
  const [selectedPriceField, setSelectedPriceField] = useState('price');
  const [isLoading, setIsLoading] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastError, setToastError] = useState(false);
  const [showHelp, setShowHelp] = useState(false);

  // Load saved pricing tools settings on component mount
  useEffect(() => {
    console.log('PricingTools component mounted, user:', user);
    if (user?.uid) {
      loadPricingSettings();
    }
  }, [user]);

  const loadPricingSettings = async () => {
    console.log('loadPricingSettings called, user:', user);
    if (!user?.uid) {
      showToastMessage('You must be logged in to load settings', true);
      return;
    }
    
    setIsLoading(true);
    try {
      const savedSettings = await UserSettingsService.getUserSettings(user.uid, 'pricing');
      if (savedSettings) {
        // Update advanced pricing tools
        setPriceTools(savedSettings);
        
        // Update basic pricing tools if they exist in saved settings
        if (savedSettings.priceChange !== undefined) {
          setPriceChange(savedSettings.priceChange);
        }
        if (savedSettings.priceChangeType !== undefined) {
          setPriceChangeType(savedSettings.priceChangeType);
        }
        if (savedSettings.selectedPriceField !== undefined) {
          setSelectedPriceField(savedSettings.selectedPriceField);
        }
        
        showToastMessage('Pricing settings loaded successfully', false);
      }
    } catch (error) {
      console.error('Error loading pricing settings:', error);
      showToastMessage('Failed to load pricing settings', true);
    } finally {
      setIsLoading(false);
    }
  };

  const savePricingSettings = async () => {
    console.log('savePricingSettings called, user:', user);
    if (!user?.uid) {
      showToastMessage('You must be logged in to save settings', true);
      return;
    }
    
    setIsLoading(true);
    try {
      // Create a complete settings object with all pricing tools
      const completeSettings = {
        ...priceTools,
        priceChange,
        priceChangeType,
        selectedPriceField
      };
      
      await UserSettingsService.saveUserSettings(user.uid, 'pricing', completeSettings);
      showToastMessage('Pricing settings saved successfully', false);
    } catch (error) {
      console.error('Error saving pricing settings:', error);
      showToastMessage('Failed to save pricing settings', true);
    } finally {
      setIsLoading(false);
    }
  };

  const showToastMessage = (message, isError = false) => {
    setToastMessage(message);
    setToastError(isError);
    setShowToast(true);
  };

  const handlePriceToolChange = (field, value) => {
    setPriceTools(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleBasicPriceChange = () => {
    if (selectedProductIds.size === 0) return;

    const selectedProducts = products.filter(p => selectedProductIds.has(p.id.toString()));
    const updates = [];

    selectedProducts.forEach(product => {
      product.variants.forEach(variant => {
        const variantId = variant.id.toString();
        const productId = product.id.toString();
        
        // Get current values from edit history using the new format
        const editedVariant = editHistory?.[productId]?.variant_updates?.[variantId] || {};
        
        // Get the current price
        let currentPrice;
        if (selectedPriceField === 'price') {
          currentPrice = editedVariant.price !== undefined ? 
            parseFloat(editedVariant.price) : 
            parseFloat(variant.price);
        } else {
          // For compare_at_price
          currentPrice = editedVariant.compare_at_price !== undefined ? 
            parseFloat(editedVariant.compare_at_price) : 
            parseFloat(variant.compareAtPrice || variant.compare_at_price || 0);
        }

        if (isNaN(currentPrice)) currentPrice = 0;
        
        // Calculate the new price based on the change type
        let newPrice = currentPrice;
        const changeValue = parseFloat(priceChange);
        
        if (!isNaN(changeValue)) {
          switch (priceChangeType) {
            case 'increase':
              // Increase by percentage
              newPrice = currentPrice * (1 + changeValue / 100);
              break;
            case 'decrease':
              // Decrease by percentage
              newPrice = currentPrice * (1 - changeValue / 100);
              break;
            case 'increase_amount':
              // Increase by fixed amount
              newPrice = currentPrice + changeValue;
              break;
            case 'decrease_amount':
              // Decrease by fixed amount
              newPrice = currentPrice - changeValue;
              break;
            case 'set':
              // Set to specific amount
              newPrice = changeValue;
              break;
            default:
              break;
          }
        }
        
        // Ensure price is not negative
        newPrice = Math.max(0, newPrice);
        
        // Round to 2 decimal places
        newPrice = parseFloat(newPrice.toFixed(2));
        
        // Add the update
        updates.push({
          productId,
          variantId,
          field: selectedPriceField,
          value: newPrice
        });
      });
    });

    console.log('Generated price updates:', updates);

    if (updates.length > 0) {
      // Apply updates using the onPriceUpdate function
      onPriceUpdate(updates);
      showToastMessage('Price updates applied successfully', false);
    }
  };

  const handleUpdatePrices = (action) => {
    if (selectedProductIds.size === 0) return;

    const selectedProducts = products.filter(p => selectedProductIds.has(p.id.toString()));
    const updates = [];

    const addUpdate = (productId, variantId, field, value) => {
      if (value === null || !isNaN(value)) {
        const update = {
          productId,
          variantId,
          field,
          value: value === null ? null : value.toFixed(2)
        };

        console.log('Adding price update:', update);
        updates.push(update);
      }
    };

    selectedProducts.forEach(product => {
      product.variants.forEach(variant => {
        // Get current values from edit history using the new format
        const variantId = variant.id.toString();
        const productId = product.id.toString();
        
        const editedVariant = editHistory?.[productId]?.variant_updates?.[variantId] || {};
        
        const currentPrice = editedVariant.price !== undefined 
          ? parseFloat(editedVariant.price)
          : parseFloat(variant.price);
          
        const currentComparePrice = editedVariant.compare_at_price !== undefined
          ? parseFloat(editedVariant.compare_at_price)
          : (parseFloat(variant.compareAtPrice) || currentPrice);

        console.log('Processing variant:', {
          productId,
          variantId,
          action,
          currentPrice,
          currentComparePrice,
          editedVariant,
          variantPrice: variant.price,
          variantComparePrice: variant.compareAtPrice
        });

        let newPrice = currentPrice;
        let newComparePrice = currentComparePrice;

        console.log('Processing variant:', {
          productId: product.id,
          variantId: variant.id,
          action,
          currentPrice,
          currentComparePrice,
          editedVariant,
          variantPrice: variant.price,
          variantComparePrice: variant.compareAtPrice
        });

        switch (action) {
          case 'setMinimum':
            const minPrice = parseFloat(priceTools.minimumPrice);
            if (!isNaN(minPrice) && currentPrice < minPrice) {
              addUpdate(productId, variantId, 'price', minPrice);
            }
            break;

          case 'setFixed':
            const fixedAmount = parseFloat(priceTools.fixedAmount);
            if (!isNaN(fixedAmount)) {
              newPrice = currentPrice + fixedAmount;
              addUpdate(productId, variantId, 'price', newPrice);
            }
            break;

          case 'setMargin':
            const margin = parseFloat(priceTools.margin) / 100;
            if (!isNaN(margin) && margin > 0 && margin < 1) {
              // Calculate price based on cost with desired margin
              // Formula: price = cost / (1 - margin)
              // For example: cost of $10 with 60% margin = $10 / (1 - 0.6) = $10 / 0.4 = $25
              const costPrice = currentPrice; // Assume current price is the cost
              const newPrice = costPrice / (1 - margin);
              addUpdate(productId, variantId, 'price', parseFloat(newPrice.toFixed(2)));
            }
            break;

          case 'setSalesPrice':
            const factor = parseFloat(priceTools.salesPriceFactor);
            if (!isNaN(factor) && factor > 0) {
              if (!isNaN(currentPrice) && currentPrice > 0) {
                newPrice = currentPrice * factor;
                addUpdate(productId, variantId, 'price', parseFloat(newPrice.toFixed(2)));
              }
            }
            break;

          case 'roundPrices':
            const roundToValue = parseFloat(priceTools.roundingValue);
            if (!isNaN(roundToValue)) {
              const basePrice = Math.floor(currentPrice);
              newPrice = basePrice + roundToValue;
              addUpdate(productId, variantId, 'price', newPrice);
              
              // Also round compare at price if it exists
              if (currentComparePrice > 0) {
                const baseComparePrice = Math.floor(currentComparePrice);
                newComparePrice = baseComparePrice + roundToValue;
                addUpdate(productId, variantId, 'compare_at_price', newComparePrice);
              }
            }
            break;

          case 'clearComparePrices':
            addUpdate(productId, variantId, 'compare_at_price', null);
            break;
            
          case 'comparePrice':
            const discountPercent = parseFloat(priceTools.compareFactor);
            if (!isNaN(discountPercent)) {
              if (!isNaN(currentPrice) && currentPrice > 0) {
                if (discountPercent === 100 || discountPercent === 0) {
                  // If 100%, remove compare at price
                  addUpdate(productId, variantId, 'compare_at_price', null);
                } else if (discountPercent > 0 && discountPercent < 100) {
                  // Calculate compare at price based on discount
                  const factor = 100 / (100 - discountPercent);
                  newComparePrice = parseFloat((currentPrice * factor).toFixed(2));
                  addUpdate(productId, variantId, 'compare_at_price', newComparePrice);
                }
              }
            }
            break;

          default:
            break;
        }
      });
    });

    console.log('Generated price updates:', updates);

    if (updates.length > 0) {
      // Apply updates using the onPriceUpdate function
      onPriceUpdate(updates);
      showToastMessage('Price updates applied successfully', false);
    }
  };

  return (
    <Card>
      <Card.Section>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '16px', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <Text variant="headingLg">Price Tools</Text>
            <Tooltip content="Tools to help you manage product prices">
              <Icon source={QuestionMarkMinor} color="base" onClick={() => setShowHelp(!showHelp)} />
            </Tooltip>
          </div>
          
          <ButtonGroup>
            <Button 
              icon={RefreshMinor} 
              onClick={loadPricingSettings} 
              disabled={isLoading || !user}
              tooltip="Load saved settings"
            >
              Load Settings
            </Button>
            <Button 
              icon={SaveMinor} 
              onClick={savePricingSettings} 
              disabled={isLoading || !user}
              primary
              tooltip="Save current settings"
            >
              Save Settings
            </Button>
          </ButtonGroup>
        </div>
        
        {showHelp && (
          <Banner title="About Price Tools" status="info" onDismiss={() => setShowHelp(false)}>
            <p>These tools help you make bulk changes to product prices.</p>
            <p>You can adjust prices by percentage or fixed amount, set minimum prices, and more.</p>
            <p>Save your settings to reuse them later.</p>
          </Banner>
        )}
        
        {/* Basic Price Change Tools */}
        <FormLayout>
          <FormLayout.Group>
            <Select
              label="Price Field"
              options={[
                { label: 'Price', value: 'price' },
                { label: 'Compare at Price', value: 'compare_at_price' }
              ]}
              value={selectedPriceField}
              onChange={(value) => {
                setSelectedPriceField(value);
                handlePriceToolChange('selectedPriceField', value);
              }}
              helpText="Select which price field to modify"
            />
            <TextField
              label="Price Change"
              value={priceChange}
              onChange={(value) => {
                setPriceChange(value);
                handlePriceToolChange('priceChange', value);
              }}
              type="number"
              helpText="Enter amount to increase/decrease price"
              autoComplete="off"
            />
            <Select
              label="Change Type"
              options={[
                { label: 'Increase by %', value: 'increase' },
                { label: 'Decrease by %', value: 'decrease' },
                { label: 'Increase by amount', value: 'increase_amount' },
                { label: 'Decrease by amount', value: 'decrease_amount' },
                { label: 'Set to specific amount', value: 'set' }
              ]}
              value={priceChangeType}
              onChange={(value) => {
                setPriceChangeType(value);
                handlePriceToolChange('priceChangeType', value);
              }}
            />
          </FormLayout.Group>
          <Button
            primary
            disabled={selectedProductIds.size === 0 || isPriceUpdating}
            onClick={handleBasicPriceChange}
          >
            Apply Price Change
          </Button>
        </FormLayout>
      </Card.Section>

      <Card.Section>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '16px' }}>
          <Text variant="headingLg">Advanced Price Tools</Text>
          <Tooltip content="Advanced tools to help you manage product prices: Set minimum prices, add fixed amounts, calculate margins, and more">
            <Icon source={QuestionMarkMinor} color="base" />
          </Tooltip>
        </div>
        <FormLayout>
          <FormLayout.Group>
            {/* Minimum Price */}
            <Stack vertical spacing="tight">
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <Text variant="headingMd">Minimum Price</Text>
                <Tooltip content="Set a minimum price for all selected products">
                  <Icon source={QuestionMarkMinor} color="base" />
                </Tooltip>
              </div>
              <TextField
                type="number"
                value={priceTools.minimumPrice}
                onChange={value => handlePriceToolChange('minimumPrice', value)}
                prefix="$"
              />
              <Button
                onClick={() => handleUpdatePrices('setMinimum')}
                disabled={isPriceUpdating}
                loading={isPriceUpdating}
              >
                Set Minimum
              </Button>
            </Stack>

            {/* Fixed Amount */}
            <Stack vertical spacing="tight">
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <Text variant="headingMd">Fixed Amount</Text>
                <Tooltip content="Add or subtract a fixed amount from the current price">
                  <Icon source={QuestionMarkMinor} color="base" />
                </Tooltip>
              </div>
              <TextField
                type="number"
                value={priceTools.fixedAmount}
                onChange={value => handlePriceToolChange('fixedAmount', value)}
                prefix="$"
              />
              <Button
                onClick={() => handleUpdatePrices('setFixed')}
                disabled={isPriceUpdating}
                loading={isPriceUpdating}
              >
                Add Fixed Amount
              </Button>
            </Stack>

            {/* Margin */}
            <Stack vertical spacing="tight">
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <Text variant="headingMd">Profit Margin</Text>
                <Tooltip content="Calculate selling price based on cost (current price) and desired profit margin. Example: $10 cost with 60% margin = $10 / (1 - 0.6) = $10 / 0.4 = $25">
                  <Icon source={QuestionMarkMinor} color="base" />
                </Tooltip>
              </div>
              <TextField
                type="number"
                value={priceTools.margin}
                onChange={value => handlePriceToolChange('margin', value)}
                suffix="%"
              />
              <Button
                onClick={() => handleUpdatePrices('setMargin')}
                disabled={isPriceUpdating}
                loading={isPriceUpdating}
              >
                Set Margin
              </Button>
            </Stack>

            {/* Sales Price Factor */}
            <Stack vertical spacing="tight">
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <Text variant="headingMd">Sales Price Factor</Text>
                <Tooltip content="Multiply current price by a factor (e.g., 1.1 for 10% increase)">
                  <Icon source={QuestionMarkMinor} color="base" />
                </Tooltip>
              </div>
              <TextField
                type="number"
                value={priceTools.salesPriceFactor}
                onChange={value => handlePriceToolChange('salesPriceFactor', value)}
              />
              <Button
                onClick={() => handleUpdatePrices('setSalesPrice')}
                disabled={isPriceUpdating}
                loading={isPriceUpdating}
              >
                Apply Factor
              </Button>
            </Stack>

            {/* Rounding */}
            <Stack vertical spacing="tight">
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <Text variant="headingMd">Round Prices</Text>
                <Tooltip content="Round down to nearest dollar and add cents (e.g., 0.99 for $XX.99 pricing)">
                  <Icon source={QuestionMarkMinor} color="base" />
                </Tooltip>
              </div>
              <TextField
                type="number"
                value={priceTools.roundingValue}
                onChange={value => handlePriceToolChange('roundingValue', value)}
                step="0.01"
              />
              <Button
                onClick={() => handleUpdatePrices('roundPrices')}
                disabled={isPriceUpdating}
                loading={isPriceUpdating}
              >
                Round Prices
              </Button>
            </Stack>

            {/* Compare at Price Discount */}
            <Stack vertical spacing="tight">
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <Text variant="headingMd">Compare at Price Discount</Text>
                <Tooltip content="Set the discount percentage (0-100%). At 100%, the compare-at price will be removed.">
                  <Icon source={QuestionMarkMinor} color="base" />
                </Tooltip>
              </div>

              <TextField
                type="number"
                value={priceTools.compareFactor}
                onChange={value => {
                  // Prevent values over 100
                  const numValue = parseFloat(value);
                  if (isNaN(numValue) || numValue <= 100) {
                    handlePriceToolChange('compareFactor', value);
                  }
                }}
                min="0"
                max="100"
                suffix="%"
              />
              <Button
                onClick={() => handleUpdatePrices('comparePrice')}
                disabled={isPriceUpdating}
                loading={isPriceUpdating}
              >
                Set Discount
              </Button>
            </Stack>

            {/* Clear Compare Prices */}
            <Stack vertical spacing="tight">
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <Text variant="headingMd">Clear Compare Prices</Text>
                <Tooltip content="Remove all compare-at prices from selected products">
                  <Icon source={QuestionMarkMinor} color="base" />
                </Tooltip>
              </div>
              <Button
                onClick={() => handleUpdatePrices('clearComparePrices')}
                disabled={isPriceUpdating}
                loading={isPriceUpdating}
              >
                Clear Compare Prices
              </Button>
            </Stack>
          </FormLayout.Group>
        </FormLayout>
      </Card.Section>

      {showToast && (
        <Toast
          content={toastMessage}
          error={toastError}
          onDismiss={() => setShowToast(false)}
          duration={3000}
        />
      )}
    </Card>
  );
}
